import { ICartItem } from "@/pages/point-of-sale/models/cart-item.model";
import { IItemSale } from "@/pages/point-of-sale/models/sale.model";
import {
  DISCOUNT_BY_PERCENTAGE,
  VAT_TYPE_EXEMPTED,
  VAT_TYPE_FREE,
  VAT_TYPE_TAXED,
  VAT_TYPE_UNAFFECTED,
} from "@/utilities";
import { roundMoney } from "@/utilities/decimal.utils";
import {
  getLineProductDiscountAmount,
  resolveLinePriceMode,
} from "./line-pricing.utils";

/** Tipos de descuento a nivel venta (boleta) */
export const SALE_DISCOUNT_BY_PRODUCT = "discount_by_product";
export const SALE_DISCOUNT_BY_POINTS = "discount_by_points";
export const SALE_MIXED_DISCOUNT = "mixed_discount";
export const SALE_GLOBAL_DISCOUNT = "global_discount";

/** Tipos permitidos a nivel ítem en el payload */
export const ITEM_DISCOUNT_BY_PERCENTAGE = "discount_by_percentage";
export const ITEM_DISCOUNT_BY_AMOUNT = "discount_by_amount";
/** Mismo valor que a nivel venta; identifica descuento por canje de puntos */
export const ITEM_DISCOUNT_BY_POINTS = SALE_DISCOUNT_BY_POINTS;

export type ItemDiscountApiType =
  | typeof ITEM_DISCOUNT_BY_PERCENTAGE
  | typeof ITEM_DISCOUNT_BY_AMOUNT;

/** Mapea el valor del UI (2, 3 o snake_case) al tipo API del ítem */
export function mapUiDiscountTypeToApi(
  discountType: string
): ItemDiscountApiType {
  if (
    discountType === DISCOUNT_BY_PERCENTAGE ||
    discountType === ITEM_DISCOUNT_BY_PERCENTAGE
  ) {
    return ITEM_DISCOUNT_BY_PERCENTAGE;
  }
  return ITEM_DISCOUNT_BY_AMOUNT;
}

export function isItemDiscountApiType(
  value: string | undefined | null
): value is ItemDiscountApiType {
  return (
    value === ITEM_DISCOUNT_BY_PERCENTAGE || value === ITEM_DISCOUNT_BY_AMOUNT
  );
}

export function itemUsesPointsDiscount(item: ICartItem): boolean {
  return (item.amountRedeemed ?? 0) > 0 || (item.pointsUsed ?? 0) > 0;
}

export function cartHasProductLineDiscount(cart: ICartItem[]): boolean {
  return cart.some((item) => resolveLinePriceMode(item) === "line_discount");
}

export function cartHasPointsDiscount(cart: ICartItem[]): boolean {
  return cart.some(
    (item) => (item.amountRedeemed ?? 0) > 0 || (item.pointsUsed ?? 0) > 0
  );
}

/** Descuentos de ítem (producto + puntos), sin descuento global */
export function sumItemLevelDiscountAmounts(cart: ICartItem[]): number {
  return cart.reduce((sum, item) => {
    let itemDiscount = 0;
    if (resolveLinePriceMode(item) === "line_discount") {
      itemDiscount += getLineProductDiscountAmount(item);
    }
    itemDiscount += item.amountRedeemed ?? 0;
    return sum + itemDiscount;
  }, 0);
}

export function resolveSaleDiscountType(
  cart: ICartItem[],
  hasGlobalDiscount: boolean
): string {
  if (hasGlobalDiscount) return SALE_GLOBAL_DISCOUNT;

  const hasProduct = cartHasProductLineDiscount(cart);
  const hasPoints = cartHasPointsDiscount(cart);

  if (hasProduct && hasPoints) return SALE_MIXED_DISCOUNT;
  if (hasProduct) return SALE_DISCOUNT_BY_PRODUCT;
  if (hasPoints) return SALE_DISCOUNT_BY_POINTS;
  return "";
}

export function cartHasItemLevelDiscounts(cart: ICartItem[]): boolean {
  return cartHasProductLineDiscount(cart) || cartHasPointsDiscount(cart);
}

export function hasGlobalDiscountApplied(
  discount: number,
  discountType: string
): boolean {
  return discount > 0 && discountType === SALE_GLOBAL_DISCOUNT;
}

/**
 * Prorratea un descuento global comercial (sobre el total CON IGV) entre las
 * líneas pagables usando su importe como base. Réplica de la fórmula del backend
 * (`prorratearDescuentoGlobalEnLineas`): las líneas con base 0 (gratuitas) no
 * reciben descuento y la última línea pagable absorbe la diferencia de redondeo.
 */
export function prorateGlobalDiscount(
  bases: number[],
  totalAmount: number
): number[] {
  const result = bases.map(() => 0);
  if (totalAmount <= 0) return result;

  const sum = bases.reduce((acc, base) => acc + (base > 0 ? base : 0), 0);
  if (sum <= 0) return result;

  let lastPayableIndex = -1;
  bases.forEach((base, index) => {
    if (base > 0) lastPayableIndex = index;
  });

  let accumulated = 0;
  bases.forEach((base, index) => {
    if (base <= 0) return;
    if (index === lastPayableIndex) {
      result[index] = roundMoney(Math.max(0, totalAmount - accumulated));
    } else {
      const value = roundMoney((totalAmount * base) / sum);
      result[index] = value;
      accumulated = roundMoney(accumulated + value);
    }
  });

  return result;
}

export interface GlobalDiscountNetByType {
  taxedNet: number;
  exemptedNet: number;
  unaffectedNet: number;
}

/**
 * Devuelve los importes netos (CON IGV) por tipo de afectación después de aplicar
 * el prorrateo del descuento global comercial sobre el carrito.
 */
export function getGlobalDiscountNetByVatType(
  cart: ICartItem[],
  discount: number
): GlobalDiscountNetByType {
  const bases = cart.map((item) =>
    item.vatType.type === VAT_TYPE_FREE ? 0 : item.totalPrice ?? 0
  );
  const prorated = prorateGlobalDiscount(bases, discount);

  let taxedNet = 0;
  let exemptedNet = 0;
  let unaffectedNet = 0;

  cart.forEach((item, index) => {
    const net = roundMoney((item.totalPrice ?? 0) - (prorated[index] ?? 0));
    switch (item.vatType.type) {
      case VAT_TYPE_TAXED:
        taxedNet = roundMoney(taxedNet + net);
        break;
      case VAT_TYPE_EXEMPTED:
        exemptedNet = roundMoney(exemptedNet + net);
        break;
      case VAT_TYPE_UNAFFECTED:
        unaffectedNet = roundMoney(unaffectedNet + net);
        break;
    }
  });

  return { taxedNet, exemptedNet, unaffectedNet };
}

/** Línea de boleta (API / listado) para resolver descuento global por línea */
export type BillLineForGlobalDiscount = {
  total_price: number;
  unit_price?: number;
  total_quantity?: number;
  descuento_global?: number;
  vat_type?: { type?: string };
};

/**
 * Resuelve la parte comercial de descuento global por línea.
 * Prioridad: `descuento_global` persistido → prorrateo si discountType es global
 * → heurística legacy (descuento de cabecera sin descuentos de ítem en total_price).
 */
export function resolveGlobalDiscountPerLine(
  items: BillLineForGlobalDiscount[],
  billDiscount: number,
  discountType?: string | null
): number[] {
  if (!items.length) return [];

  const hasPerLine = items.some((item) => (item.descuento_global ?? 0) > 0);
  if (hasPerLine) {
    return items.map((item) => roundMoney(Math.max(0, item.descuento_global ?? 0)));
  }

  const bases = items.map((item) =>
    item.vat_type?.type === VAT_TYPE_FREE ? 0 : item.total_price || 0
  );

  if (discountType === SALE_GLOBAL_DISCOUNT && billDiscount > 0) {
    return prorateGlobalDiscount(bases, billDiscount);
  }

  // Legacy: listados sin discountType — solo prorratear si el descuento de cabecera
  // no está ya embebido en total_price (producto/puntos).
  if ((!discountType || discountType === "") && billDiscount > 0) {
    const subTotal = items.reduce((acc, item) => acc + (item.total_price || 0), 0);
    const listSubTotal = items.reduce(
      (acc, item) =>
        acc + (item.unit_price ?? 0) * (item.total_quantity ?? 0),
      0
    );
    const lineDiscountAlreadyApplied = listSubTotal - subTotal > 0.001;
    if (!lineDiscountAlreadyApplied) {
      return prorateGlobalDiscount(bases, billDiscount);
    }
  }

  return items.map(() => 0);
}

export function getNetLineTotalPrice(
  grossTotalPrice: number,
  globalDiscountLine: number
): number {
  return Math.max(0, roundMoney(grossTotalPrice - (globalDiscountLine || 0)));
}

export const GLOBAL_DISCOUNT_EXCLUSIVE_MSG =
  "No se puede aplicar descuento global si ya hay descuentos por producto o por puntos en la venta.";

export const ITEM_DISCOUNT_EXCLUSIVE_MSG =
  "No se puede aplicar descuento por producto o puntos mientras haya un descuento global en la boleta.";

export function getReceiptUnitPrice(item: IItemSale): number {
  if (item.discountType === "discount_by_price") {
    const qty = item.quantity > 0 ? item.quantity : 1;
    return Number((item.unitPrice - item.discountedAmount / qty).toFixed(2));
  }
  return item.unitPrice;
}

export function getItemLineDiscountMoneyAmount(
  item: ICartItem,
  unitPrice: number,
  quantity: number,
  discountType: string,
  discountedAmount: number
): number {
  if (!discountType || discountedAmount <= 0) return 0;

  if (resolveLinePriceMode(item) === "line_discount") {
    return getLineProductDiscountAmount(item);
  }

  const qty = quantity || 1;
  switch (discountType) {
    case ITEM_DISCOUNT_BY_PERCENTAGE:
      return Number(
        ((unitPrice * qty * Math.abs(discountedAmount)) / 100).toFixed(2)
      );
    case ITEM_DISCOUNT_BY_AMOUNT:
      return Number((Math.abs(discountedAmount) * qty).toFixed(2));
    default:
      return 0;
  }
}
